import { useEffect, useState } from "react";
import { useQuery } from "convex/react";
import { Search } from "lucide-react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ChatSidebar } from "./Sidebar";
import { ConversationView } from "./ConversationView";
import { SearchPalette } from "./SearchPalette";

type Props = {
  activeId: Id<"conversations"> | null;
  setActiveId: (id: Id<"conversations"> | null) => void;
};

export function AppShell({ activeId, setActiveId }: Props) {
  const [searchOpen, setSearchOpen] = useState(false);
  const me = useQuery(api.users.me);
  const list = useQuery(api.conversations.listMine);

  useEffect(() => {
    if (activeId === null && list && list.length > 0) {
      setActiveId(list[0]!.conversation._id);
    }
  }, [activeId, list, setActiveId]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  if (!me) return null;

  return (
    <SidebarProvider>
      <ChatSidebar
        meId={me._id}
        activeId={activeId}
        setActiveId={setActiveId}
        openSearch={() => setSearchOpen(true)}
      />
      <SidebarInset className="h-screen">
        <header className="flex h-12 shrink-0 items-center gap-2 border-b px-3">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mx-1 h-4" />
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground gap-2"
            onClick={() => setSearchOpen(true)}
          >
            <Search className="size-4" />
            Search messages
            <kbd className="ml-2 hidden md:inline-flex h-5 items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px]">
              ⌘K
            </kbd>
          </Button>
        </header>
        <div className="flex-1 min-h-0">
          {activeId ? (
            <ConversationView conversationId={activeId} meId={me._id} />
          ) : (
            <EmptyState />
          )}
        </div>
      </SidebarInset>
      <SearchPalette
        open={searchOpen}
        onOpenChange={setSearchOpen}
        onSelect={(id) => {
          setActiveId(id);
          setSearchOpen(false);
        }}
      />
    </SidebarProvider>
  );
}

function EmptyState() {
  return (
    <div className="h-full flex items-center justify-center text-center p-8">
      <div className="max-w-sm space-y-2">
        <h2 className="text-lg font-medium">No conversation selected</h2>
        <p className="text-sm text-muted-foreground">
          Pick a room or DM from the sidebar, or create a new one.
        </p>
      </div>
    </div>
  );
}
